import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { flat, listOf } from '../entity/entity';
import { faceLine } from '../plan/arc';
import type { Arc } from '../plan/arc';
import type { BoardRow } from '../plan/board';
import { arcHref } from '../routes/ArcRoute';
import { ArchivedGlyph, StatusGlyph, StorylineGlyph } from './StatusGlyph';
import { FolderLink, useFolders } from '../folders/FolderChip';
import { CardTagMark } from '../folders/TagMark';
import type { TagMark } from '../folders/TagMark';

/**
 * The arc as a CARD — the holder for a surface where the arc IS the content.
 *
 * Same entity as `ArcRow`, different holder: a card carries the eyebrow run,
 * the title, the one load-bearing line and a footer, because the wall is what
 * the page is about. The queue keeps rows, since a pointer out deserves less
 * than the thing being pointed at.
 *
 * A storyline's members live INSIDE its card rather than in a tree beside it.
 * Depth is a crumb, never an indentation ladder, and the card never insets its
 * members — a member wraps its text exactly as a loose arc does.
 */

const MEMBER_CAP = 4;

export function ArcCard({
  arc,
  members,
  tint,
  mark,
  plain = false,
  crumb,
  focused = false,
  selected = false,
  dragging = false,
  dropBefore = false,
  movable = false,
  onPointerDown,
  onOpenPicker,
  onMenu,
}: {
  arc: Arc;
  members?: BoardRow[];
  tint?: { fill: string; border: string };
  /** Its tag's mark, at the end of the head line. */
  mark?: TagMark | null;
  plain?: boolean;
  crumb?: string;
  focused?: boolean;
  selected?: boolean;
  dragging?: boolean;
  dropBefore?: boolean;
  movable?: boolean;
  onPointerDown?: (event: ReactPointerEvent) => void;
  onOpenPicker?: (anchor: DOMRect) => void;
  onMenu?: (arc: Arc, event: React.MouseEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const held = members ?? [];
  const shown = open ? held : held.slice(0, MEMBER_CAP);
  const done = arc.status === 'done';
  const area = flat(arc.frontMatter['area']);
  const line = faceLine(arc);

  return (
    <article
      className={
        `arccard${plain ? ' is-plain' : ''}${done ? ' is-done' : ''}` +
        `${focused ? ' focused' : ''}${selected ? ' selected' : ''}` +
        `${dragging ? ' dragging' : ''}${dropBefore ? ' drop-before' : ''}`
      }
      data-arc-name={movable ? arc.name : undefined}
      style={tint && !plain ? { background: tint.fill, borderColor: tint.border } : undefined}
      onPointerDown={movable ? onPointerDown : undefined}
      onContextMenu={
        onMenu &&
        ((event) => {
          event.preventDefault();
          onMenu(arc, event);
        })
      }
    >
      <div className="arccard-head t-metaMono">
        <button
          type="button"
          className="status-button"
          title={
            arc.archived ? `archived — still ${arc.status} in its file` : `${arc.status} — change`
          }
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => onOpenPicker?.(event.currentTarget.getBoundingClientRect())}
        >
          {arc.archived ? <ArchivedGlyph /> : <StatusGlyph status={arc.status} />}
        </button>
        {held.length > 0 && <StorylineGlyph />}
        {area && <span className="t-eyebrowMono arccard-area">{area}</span>}
        {held.length > 0 && <span>· {held.length} inside</span>}
        <span className="arccard-headspace" />
        {mark && <CardTagMark mark={mark} size={12} />}
        {arc.frontMatter['updated'] && (
          <span className="arccard-updated">{flat(arc.frontMatter['updated'])}</span>
        )}
      </div>

      <span className="arccard-kebab" aria-hidden="true">
        ⋯
      </span>

      {crumb && <div className="arccard-crumb">{crumb} ›</div>}

      <h3 className="arccard-title">
        <Link to={arcHref(arc.name)}>{arc.title}</Link>
      </h3>

      {line.text && (
        <p
          className={
            `arccard-prose${parked(arc) ? ' is-parked' : ''}${line.asking ? ' is-asking' : ''}`
          }
        >
          {line.starting && <span className="t-eyebrowMono prose-label">start with</span>}
          {line.text}
        </p>
      )}

      <Footer arc={arc} />

      {held.length > 0 && (
        <div className="arccard-members">
          {shown.map((row) => (
            <Link key={row.arc.path} to={arcHref(row.arc.name)} className="arccard-member">
              <StatusGlyph status={row.arc.status} size={10} />
              {row.depth > 1 && <span className="arccard-member-crumb">deeper ›</span>}
              <span className="arccard-member-title">{row.arc.title}</span>
              <span className="t-metaMono arccard-member-updated">
                {flat(row.arc.frontMatter['updated'])}
              </span>
            </Link>
          ))}
          {!open && held.length > MEMBER_CAP && (
            <button
              type="button"
              className="arccard-more t-metaMono"
              onClick={() => setOpen(true)}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {held.length - MEMBER_CAP} more inside
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function parked(arc: Arc): boolean {
  return arc.status === 'parked';
}

function Footer({ arc }: { arc: Arc }) {
  const folders = useFolders();
  const tags = listOf(arc.frontMatter['tags']);
  const markers = listOf(arc.frontMatter['markers']);
  if (tags.length === 0 && markers.length === 0) return null;

  return (
    <div className="arccard-footer t-metaMono">
      {tags.map((tag) => (
        <FolderLink key={tag} name={tag} folders={folders} className="meta-tag">
          {tag}
        </FolderLink>
      ))}
      {markers.map((marker) => (
        <span key={marker} className="meta-marker">
          ⌇ {marker}
        </span>
      ))}
    </div>
  );
}
